/**
 * image block reference: @see https://www.briancoords.com/managing-images-in-your-custom-gutenberg-block/
 */

/**
 * Retrieves the translation of text.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-i18n/
 */
import { __ } from '@wordpress/i18n';

/**
 * React hook that is used to mark the block wrapper element.
 * It provides all the necessary props like the class name.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#useblockprops
 */
import { useBlockProps, InspectorControls, RichText } from '@wordpress/block-editor';
import { Panel, PanelBody, PanelRow, Button, DatePicker, RadioControl } from '@wordpress/components';
import Search from '../Search';
import { Image } from '@10up/block-components';

/**
 * Lets webpack process CSS, SASS or SCSS files referenced in JavaScript files.
 * Those files can contain any CSS code that gets applied to the editor.
 *
 * @see https://www.npmjs.com/package/@wordpress/scripts#using-css
 */
import '../index.css';
import { useSelect } from '@wordpress/data';

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @return {WPElement} Element to render.
 */
export default function Edit({ attributes, setAttributes }) {
	const {
		force,
		schaatsen,
		skeeleren
	} = attributes;


	// initiating default values when block is created
	if (!schaatsen?.date) {
		setAttributes({ schaatsen: { ...schaatsen, date: new Date(sport_highlight_block.startHerfst.date) } })
	}
	if (!skeeleren?.date) {
		setAttributes({ skeeleren: { ...skeeleren, date: new Date(sport_highlight_block.startLente.date) } })
	}
	if (!force) {
		setAttributes({ force: 'none' })
	}


	// calculating which sport to show based on dates
	let output

	if (force === "none") {

		const current = new Date()
		const today = current.getTime()
		const firstDay = new Date('01-01-' + current.getFullYear())
		const lastDay = new Date('12-31-' + current.getFullYear())
		const schaatsenDate = new Date(schaatsen?.date || null)
		const skeelerenDate = new Date(skeeleren?.date || null)


		if (today >= firstDay && today < skeelerenDate) {
			output = 'schaatsen'
		} else if (today >= skeelerenDate && today < schaatsenDate) {
			output = 'skeeleren';
		} else if (today >= schaatsenDate && today <= lastDay) {
			output = 'schaatsen';
		} else {
			output = 'error';
		}

	} else {
		output = force
	}

	// fetching images
	const [skeelerImage, schaatsImage] = useSelect((select) => [skeeleren?.image, schaatsen?.image].map(id => {
		if (id) {
			return select('core').getMedia(id)
		} else {
			return undefined
		}
	}))


	return (
		<div {...useBlockProps()}>
			<InspectorControls>
				<Panel>
					<PanelBody title='Settings'>
						<PanelRow>
							<RadioControl
								label="Force show sport"
								onChange={(value) => setAttributes({ force: value })}
								options={[
									{
										label: 'None',
										value: 'none'
									},
									{
										label: 'Schaatsen',
										value: 'schaatsen'
									},
									{
										label: 'Skeeleren',
										value: 'skeeleren'
									}
								]}
								selected={force || 'none'}
							/>
						</PanelRow>
					</PanelBody>

					<PanelBody title='Schaatsen'>

						<PanelRow>
							<Search
								label="Schaatsen button link"
								attribute={schaatsen?.link}
								setAttribute={link => setAttributes({ schaatsen: { ...schaatsen, link } })}
							/>
						</PanelRow>
						<hr></hr>

						<PanelRow>
							Theme value for start fall is {new Date(sport_highlight_block.startHerfst.date).toLocaleDateString('nl-NL')}
						</PanelRow>

						<PanelRow>
							<DatePicker
								label="Start of speedskating season"
								startOfWeek={1}
								currentDate={schaatsen?.date || ""}
								onChange={(date) => setAttributes({ schaatsen: { ...schaatsen, date } })}
							/>
						</PanelRow>
						<hr></hr>

						<PanelRow>

							<Image
								id={schaatsen?.image || ""}
								className="my-image"
								size="full"
								onSelect={(image) => setAttributes({ schaatsen: { ...schaatsen, image: image.id } })}
								labels={{
									title: 'Select Schaatsen Image',
									instructions: 'Upload a media file or pick one from your media library.'
								}}
							/>

						</PanelRow>
						<PanelRow>
							<Button isDestructive variant="link" onClick={() => setAttributes({ schaatsen: { ...schaatsen, image: null } })}>Remove Schaatsen Image</Button>
						</PanelRow>

					</PanelBody>


					<PanelBody title='Skeeleren'>

						<PanelRow>
							<Search
								label="Skeeleren button link"
								attribute={skeeleren?.link}
								setAttribute={link => setAttributes({ skeeleren: { ...skeeleren, link } })}
							/>
						</PanelRow>
						<hr></hr>

						<PanelRow>
							Theme value for start spring is {new Date(sport_highlight_block.startLente.date).toLocaleDateString('nl-NL')}
						</PanelRow>

						<PanelRow>
							<DatePicker
								label="Start of inline skating season"
								startOfWeek={1}
								currentDate={skeeleren?.date || ""}
								onChange={(date) => setAttributes({ skeeleren: { ...skeeleren, date } })}
							/>
						</PanelRow>
						<hr></hr>

						<PanelRow>

							<Image
								id={skeeleren?.image || ""}
								className="my-image"
								size="full"
								onSelect={(image) => setAttributes({ skeeleren: { ...skeeleren, image: image.id } })}
								labels={{
									title: 'Select Skeeleren Image',
									instructions: 'Upload a media file or pick one from your media library.'
								}}
							/>

						</PanelRow>

						<PanelRow>
							<Button isDestructive variant="link" onClick={() => setAttributes({ skeeleren: { ...skeeleren, image: null } })}>Remove Skeeleren Image</Button>
						</PanelRow>
					</PanelBody>

				</Panel>
			</InspectorControls>




			{/* Schaatsen */}
			<div className="w-full flex justify-center" style={{ opacity: output == "skeeleren" && "50%" }}>

				<div className="max-w-screen w-full my-10 grid grid-rows-2 lg:grid-rows-1 lg:grid-cols-2 gap-10">
					{/* image */}
					<div className="max-w-screen flex justify-center lg:justify-end">


						{/* schaats svg */}
						<div className="w-full max-w-[36rem] relative">
							{/* red */}
							<svg className="absolute -z-10 left-3 -right-3 top-3 -bottom-3" viewBox=" 0 0 132 56" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path fill="#E04712" d="M25.4139 22.4316C25.8061 18.9776 27.6397 15.3724 28.3514 12.9736C29.2353 9.9944 29.9265 6.25415 31.2491 5.14833C32.5716 4.04251 40.6597 0.211104 46.5014 0.0160296C52.3432 -0.179044 58.0055 1.42794 62.0462 3.65871C66.0869 5.88948 71.078 12.0413 80.0433 16.7139C89.0086 21.3865 98.9443 22.991 101.164 25.3718C103.384 27.7526 102.985 31.662 100.845 32.7873C98.705 33.9126 93.5544 34.4395 90.9758 35.103C88.5522 35.7266 87.5376 36.4479 86.3926 36.9698H89.5735L94.3719 42.1542V43.5953H124.251C125.793 43.5953 128.378 44.4214 129.701 45.2475C131.023 46.0736 132 47.1795 132 47.8755V52.0451C132 52.9427 131.808 54.1006 130.831 54.9527C129.854 55.8049 128.398 56 127.66 56H1.93958C1.27499 56 0.969847 55.9219 0.491051 55.4211C0.0122554 54.9202 -0.0541594 54.5234 0.0322079 54.0811L1.64052 45.7354C1.82025 44.7467 3.44185 44.1807 3.98017 44.0246C4.51849 43.8685 5.62836 43.6278 7.23667 43.5953H24.3905L26.7564 42.1122H31.2607L32.3921 39.9295C32.3921 39.9295 31.8671 40.0726 31.3354 39.5718C30.8038 39.0709 30.999 38.4269 30.999 38.4269L32.0807 32.7908C29.9537 31.6984 25.4139 27.8803 25.4139 22.4316Z" />
							</svg>

							{/* image */}
							<svg viewBox="0 0 132 56" fill="none" xmlns="http://www.w3.org/2000/svg">
								<defs>
									<pattern id="schaats_image" patternUnits="userSpaceOnUse" width="132" height="56">
										<image xlinkHref={schaatsImage && schaatsImage.source_url} x="0" y="0" width="100%" height="100%" preserveAspectRatio="xMinYMid slice" />
									</pattern>
								</defs>
								<path fill="url(#schaats_image)" d="M25.4139 22.4316C25.8061 18.9776 27.6397 15.3724 28.3514 12.9736C29.2353 9.9944 29.9265 6.25415 31.2491 5.14833C32.5716 4.04251 40.6597 0.211104 46.5014 0.0160296C52.3432 -0.179044 58.0055 1.42794 62.0462 3.65871C66.0869 5.88948 71.078 12.0413 80.0433 16.7139C89.0086 21.3865 98.9443 22.991 101.164 25.3718C103.384 27.7526 102.985 31.662 100.845 32.7873C98.705 33.9126 93.5544 34.4395 90.9758 35.103C88.5522 35.7266 87.5376 36.4479 86.3926 36.9698H89.5735L94.3719 42.1542V43.5953H124.251C125.793 43.5953 128.378 44.4214 129.701 45.2475C131.023 46.0736 132 47.1795 132 47.8755V52.0451C132 52.9427 131.808 54.1006 130.831 54.9527C129.854 55.8049 128.398 56 127.66 56H1.93958C1.27499 56 0.969847 55.9219 0.491051 55.4211C0.0122554 54.9202 -0.0541594 54.5234 0.0322079 54.0811L1.64052 45.7354C1.82025 44.7467 3.44185 44.1807 3.98017 44.0246C4.51849 43.8685 5.62836 43.6278 7.23667 43.5953H24.3905L26.7564 42.1122H31.2607L32.3921 39.9295C32.3921 39.9295 31.8671 40.0726 31.3354 39.5718C30.8038 39.0709 30.999 38.4269 30.999 38.4269L32.0807 32.7908C29.9537 31.6984 25.4139 27.8803 25.4139 22.4316Z" />
							</svg>
						</div>


					</div>


					{/* text */}
					<div className="w-full max-w-96 row-span-2 lg:row-span-1 flex flex-col m-auto text-center lg:text-left">

						<RichText
							tagName='h1'
							value={schaatsen?.title}
							allowedFormats={[]}
							onChange={(title) => setAttributes({ schaatsen: { ...schaatsen, title } })}
							placeholder="Insert title"
							className="text-4xl font-extrabold mb-3"
						/>

						<RichText
							tagName='p'
							value={schaatsen?.text}
							allowedFormats={[]}
							onChange={(text) => setAttributes({ schaatsen: { ...schaatsen, text } })}
							placeholder="Insert text"
							className="pb-5"
						/>

						<a className="inline-flex items-center whitespace-nowrap bg-o-red text-white text-lg m-auto px-5 py-2 rounded-full hover:cursor-pointer hover:opacity-80 transition-all decoration-transparent">
							<RichText
								tagName='p'
								value={schaatsen?.button}
								allowedFormats={[]}
								onChange={(button) => setAttributes({ schaatsen: { ...schaatsen, button } })}
								placeholder="Insert text"
								className="!text-white"
							/>
							<svg className="w-6 h-6 ml-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
								<path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 12H5m14 0-4 4m4-4-4-4" />
							</svg>
						</a>

					</div>
				</div>
			</div>




			{/* Skeeleren */}
			<div className="w-full flex justify-center" style={{ opacity: output == "schaatsen" && "50%" }}>

				<div className="max-w-screen w-full my-10 grid grid-rows-2 lg:grid-rows-1 lg:grid-cols-2 gap-10">
					{/* image */}
					<div className="max-w-screen flex justify-center lg:justify-end">


						{/* skeeler svg */}
						<div className="w-full max-w-[30rem] relative">
							{/* red */}
							<svg className="absolute -z-10 left-3 -right-3 top-3 -bottom-3" viewBox=" 0 0 98 57" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path fill="#E04712" d="M23.7179 5.34232C23.9079 5.98032 23.7993 7.64447 23.5098 8.52927C23.419 8.80697 23.0606 9.50237 22.5951 10.4056C21.4326 12.6612 19.6021 16.2128 19.6021 17.7923C19.6021 20.5549 20.1479 24.2835 20.5881 25.6017C20.8633 26.4255 21.4309 27.3817 21.8876 28.1511C22.0571 28.4365 22.2113 28.6962 22.3296 28.9139L18.2666 34.0613C16.4547 32.9762 14.3338 32.3523 12.0669 32.3523C5.40252 32.3523 0 37.7444 0 44.396C0 51.0475 5.40252 56.4396 12.0669 56.4396C18.7312 56.4396 24.1337 51.0475 24.1337 44.396C24.1337 43.9345 24.1077 43.4791 24.0571 43.0312H24.6931C24.6462 43.4629 24.6221 43.9015 24.6221 44.3457C24.6221 50.9973 30.0246 56.3894 36.689 56.3894C43.3533 56.3894 48.7558 50.9973 48.7558 44.3457C48.7558 44.0279 48.7435 43.713 48.7193 43.4013H49.2807C49.2565 43.713 49.2442 44.0279 49.2442 44.3457C49.2442 50.9973 54.6467 56.3894 61.311 56.3894C67.9754 56.3894 73.3779 50.9973 73.3779 44.3457C73.3779 44.0279 73.3656 43.713 73.3414 43.4013H73.9028L73.9015 43.4191C73.8782 43.7249 73.8663 44.0339 73.8663 44.3457C73.8663 50.9973 79.2688 56.3894 85.9331 56.3894C92.5975 56.3894 98 50.9973 98 44.3457C98 37.6942 92.5975 32.302 85.9331 32.302C83.6153 32.302 81.4501 32.9543 79.6118 34.0849L75.6844 31.6195H75.604L77.1956 26.7393C77.8559 26.2878 79.0337 25.2587 79.1494 23.453C79.2942 21.1959 77.1956 19.9952 75.2508 19.5618C74.9528 19.4954 74.4563 19.4127 73.8048 19.3042C70.2046 18.7044 61.8737 17.3165 56.1826 13.5399C50.7891 9.96089 45.5812 4.09642 43.4554 1.66782C41.4262 0.795084 36.0109 -0.372596 32.2298 0.114903C28.4515 0.602049 25.0868 3.81053 23.7179 5.34232Z" />
							</svg>

							{/* image */}
							<svg viewBox="0 0 98 57" fill="none" xmlns="http://www.w3.org/2000/svg">
								<defs>
									<pattern id="skeeler_image" patternUnits="userSpaceOnUse" width="98" height="57">
										<image xlinkHref={skeelerImage && skeelerImage.source_url} x="0" y="0" width="100%" height="100%" preserveAspectRatio="xMinYMid slice" />
									</pattern>
								</defs>
								<path fill="url(#skeeler_image)" d="M23.7179 5.34232C23.9079 5.98032 23.7993 7.64447 23.5098 8.52927C23.419 8.80697 23.0606 9.50237 22.5951 10.4056C21.4326 12.6612 19.6021 16.2128 19.6021 17.7923C19.6021 20.5549 20.1479 24.2835 20.5881 25.6017C20.8633 26.4255 21.4309 27.3817 21.8876 28.1511C22.0571 28.4365 22.2113 28.6962 22.3296 28.9139L18.2666 34.0613C16.4547 32.9762 14.3338 32.3523 12.0669 32.3523C5.40252 32.3523 0 37.7444 0 44.396C0 51.0475 5.40252 56.4396 12.0669 56.4396C18.7312 56.4396 24.1337 51.0475 24.1337 44.396C24.1337 43.9345 24.1077 43.4791 24.0571 43.0312H24.6931C24.6462 43.4629 24.6221 43.9015 24.6221 44.3457C24.6221 50.9973 30.0246 56.3894 36.689 56.3894C43.3533 56.3894 48.7558 50.9973 48.7558 44.3457C48.7558 44.0279 48.7435 43.713 48.7193 43.4013H49.2807C49.2565 43.713 49.2442 44.0279 49.2442 44.3457C49.2442 50.9973 54.6467 56.3894 61.311 56.3894C67.9754 56.3894 73.3779 50.9973 73.3779 44.3457C73.3779 44.0279 73.3656 43.713 73.3414 43.4013H73.9028L73.9015 43.4191C73.8782 43.7249 73.8663 44.0339 73.8663 44.3457C73.8663 50.9973 79.2688 56.3894 85.9331 56.3894C92.5975 56.3894 98 50.9973 98 44.3457C98 37.6942 92.5975 32.302 85.9331 32.302C83.6153 32.302 81.4501 32.9543 79.6118 34.0849L75.6844 31.6195H75.604L77.1956 26.7393C77.8559 26.2878 79.0337 25.2587 79.1494 23.453C79.2942 21.1959 77.1956 19.9952 75.2508 19.5618C74.9528 19.4954 74.4563 19.4127 73.8048 19.3042C70.2046 18.7044 61.8737 17.3165 56.1826 13.5399C50.7891 9.96089 45.5812 4.09642 43.4554 1.66782C41.4262 0.795084 36.0109 -0.372596 32.2298 0.114903C28.4515 0.602049 25.0868 3.81053 23.7179 5.34232Z" />

							</svg>
						</div>


					</div>


					{/* text */}
					<div className="w-full max-w-96 row-span-2 lg:row-span-1 flex flex-col m-auto text-center lg:text-left">

						<RichText
							tagName='h1'
							value={skeeleren?.title}
							allowedFormats={[]}
							onChange={(title) => setAttributes({ skeeleren: { ...skeeleren, title } })}
							placeholder="Insert title"
							className="text-4xl font-extrabold mb-3"
						/>

						<RichText
							tagName='p'
							value={skeeleren?.text}
							allowedFormats={[]}
							onChange={(text) => setAttributes({ skeeleren: { ...skeeleren, text } })}
							placeholder="Insert text"
							className="pb-5"
						/>

						<a className="inline-flex items-center whitespace-nowrap bg-o-red text-white text-lg m-auto px-5 py-2 rounded-full hover:cursor-pointer hover:opacity-80 transition-all decoration-transparent">
							<RichText
								tagName='p'
								value={skeeleren?.button}
								allowedFormats={[]}
								onChange={(button) => setAttributes({ skeeleren: { ...skeeleren, button } })}
								placeholder="Insert text"
								className="!text-white"
							/>
							<svg className="w-6 h-6 ml-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
								<path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 12H5m14 0-4 4m4-4-4-4" />
							</svg>
						</a>

					</div>

				</div>
			</div>




		</div>
	);
}